import axios, { AxiosError } from "axios";
import type { InternalAxiosRequestConfig } from "axios";
import { storeToRefs } from "pinia";
import {
  EErrorType,
  type ErrorSchema,
  isTokenNotValidError,
  TERMS_NOT_ACCEPTED_ERROR_CODE,
  TERMS_QUERY_KEY_ROOT,
  // djangoErrorHandler,
} from "@/utils/DjangoError";
// import { useSnackStore } from "@/stores/snackStore";
import { TREATMENT_PLAN_JOB_ORIGIN } from "@/api/models/treatmentPlan/jobConstants";
import { useAuthStore } from "@/stores/authStore";
import { usePendingTermsStore } from "@/stores/pendingTermsStore";
import { queryClient } from "@/lib/queryClient";
import { i18n } from "@/locales";

export const HTTP = axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value == null) return;
      if (Array.isArray(value)) {
        if (value.length > 0) searchParams.append(key, value.join(","));
      } else {
        searchParams.append(key, String(value));
      }
    });
    return searchParams.toString();
  },
});

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Add authorization header to every request
HTTP.interceptors.request.use(async function (config) {
  const { accessToken, isLoggedIn } = storeToRefs(useAuthStore());

  if (isLoggedIn.value && accessToken.value) {
    config.headers.set("Authorization", `Bearer ${accessToken.value}`);
  }

  const method = (config.method || "").toLowerCase();
  if (["post", "patch"].includes(method)) {
    // Get job_id from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const jobId = urlParams.get(TREATMENT_PLAN_JOB_ORIGIN);

    if (jobId) {
      config.params = { ...config.params, [TREATMENT_PLAN_JOB_ORIGIN]: jobId };
    }
  }

  return config;
});

// Intercept response errors
HTTP.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorSchema>) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    if (
      isTokenNotValidError(error) &&
      !originalRequest?._retry &&
      !originalRequest?.url?.includes("/auth/jwt/refresh/")
    ) {
      originalRequest!._retry = true;
      const authStore = useAuthStore();

      const newToken = await authStore.refreshTokenIfNeeded(true);

      if (newToken) {
        originalRequest!.headers.set("Authorization", `Bearer ${newToken}`);
        return HTTP(originalRequest!);
      }
    }

    if (
      error.response?.status === 403 &&
      error.response?.data.type === EErrorType.CLIENT_ERROR &&
      error.response?.data.errors.some((e) => e.code === TERMS_NOT_ACCEPTED_ERROR_CODE) &&
      // Only block a live session. Requests already in flight when the user declines and logs out
      // land here after the token is gone, and re-arming the block then would resurrect the dialog
      // over the login screen — and, because `isBlocked` gates every query, keep the app wedged.
      useAuthStore().isLoggedIn
    ) {
      const pending = error.response.data.errors
        .filter((e) => e.code === TERMS_NOT_ACCEPTED_ERROR_CODE)
        .flatMap((e) => e.pending ?? []);
      usePendingTermsStore().triggerBlock(pending);
      // Spare the terms queries themselves: a cancelled query settles in a non-success state and
      // never auto-retries, so cancelling these would leave the acceptance UI with no document
      // body to render (see `ignorePendingTermsBlock`, which keeps them enabled for the same
      // reason). Everything else is cancelled — it would only 403 again anyway.
      queryClient.cancelQueries({
        predicate: (query) => query.queryKey[0] !== TERMS_QUERY_KEY_ROOT,
      });
      return Promise.reject(error);
    }

    if (error.code === "ECONNABORTED") {
      const t = i18n.global.t;
      const ms = error.config?.timeout ?? 0;
      error.message = t("generic.errors.requestTimeout", { ms });
    }

    // if (error.response && error.response.data && !originalRequest?.params?.suppress_errors) {
    //   const snackStore = useSnackStore();
    //   const handleError = djangoErrorHandler(snackStore);
    //   handleError(error);
    // }

    logError(error, {
      context: {
        feature: "http",
        step: "responseInterceptor",
        url: originalRequest?.url ?? "",
        method: originalRequest?.method ?? "",
        status: error.response?.status ?? null,
      },
    });

    return Promise.reject(error);
  }
);
